/**
 * Hook for estimating slippage on market orders
 * Calculates progressive slippage based on order size and VLP configuration
 */

import { useMemo } from "react";
import { getAdditionalSpreadBps, getSuggestedTolerance } from "./slippage-config";

interface SlippageEstimationParams {
  orderSize: number; // Order size in base asset (e.g., BTC)
  market: string;
  currentPrice: number; // Current market price
  vlpQuote: {
    bid_price: string;
    ask_price: string;
    spread: string;
    oracle_price: string;
  } | null;
  userTolerance: number | null; // User's max slippage tolerance (as decimal, e.g., 0.01 = 1%), null = Auto (no limit)
  isBuy?: boolean; // True for buy orders, false for sell (default: true)
}

export interface SlippageEstimation {
  // Core calculations
  estimatedSlippageBps: number;
  estimatedSlippagePercent: number;
  estimatedExecutionPrice: number;
  
  // Spread breakdown
  baseSpreadBps: number;
  sizeBasedSpreadBps: number;
  
  // Validation
  exceedsTolerance: boolean;
  currentTolerance: number | null;
  suggestedTolerance: number;
  
  // Loading states
  isLoading: boolean;
  hasVlpData: boolean;
}

/**
 * Calculate estimated slippage for a market order
 * Combines base VLP spread with progressive size-based slippage
 */
export function useSlippageEstimation({
  orderSize,
  market,
  currentPrice,
  vlpQuote,
  userTolerance,
  isBuy = true,
}: SlippageEstimationParams): SlippageEstimation {
  return useMemo(() => {
    // Default state when no data available
    if (!vlpQuote || !currentPrice || orderSize <= 0) {
      return {
        estimatedSlippageBps: 0,
        estimatedSlippagePercent: 0,
        estimatedExecutionPrice: currentPrice,
        baseSpreadBps: 0,
        sizeBasedSpreadBps: 0,
        exceedsTolerance: false,
        currentTolerance: userTolerance,
        suggestedTolerance: userTolerance ?? 0.001, // Default to 0.1% when Auto
        isLoading: false,
        hasVlpData: !!vlpQuote,
      };
    }

    // Parse VLP quote data
    const oraclePrice = parseFloat(vlpQuote.oracle_price);
    const spread = parseFloat(vlpQuote.spread);

    // Calculate base spread in basis points
    // Base spread is the VLP spread (bid-ask difference)
    const baseSpreadBps = oraclePrice > 0 
      ? (spread / oraclePrice) * 10000 
      : 0;

    // Calculate order size in USD
    const orderSizeUsd = orderSize * currentPrice;

    // Calculate additional spread from progressive tiers
    const sizeBasedSpreadBps = getAdditionalSpreadBps(orderSizeUsd);

    // Total slippage combines base spread + size-based spread
    const totalSlippageBps = baseSpreadBps + sizeBasedSpreadBps;
    const slippagePercent = totalSlippageBps / 100; // Convert bps to %

    // Calculate half spread (applied to one side of the trade)
    const totalSpread = (totalSlippageBps / 10000) * oraclePrice;
    const halfSpread = totalSpread / 2;

    // Estimated execution price
    // Buy orders: pay ask side (oracle + half spread)
    // Sell orders: receive bid side (oracle - half spread)
    const estimatedExecutionPrice = isBuy
      ? oraclePrice + halfSpread
      : oraclePrice - halfSpread;

    // Check if slippage exceeds user's tolerance
    // Slippage percent is for one-way (half spread), user tolerance is total
    // Auto mode (null) = never exceeds tolerance
    const exceedsTolerance = userTolerance !== null && slippagePercent > userTolerance * 100;

    // Suggest appropriate tolerance for this order size
    const suggestedTolerance = getSuggestedTolerance(orderSizeUsd);

    return {
      estimatedSlippageBps: totalSlippageBps,
      estimatedSlippagePercent: slippagePercent,
      estimatedExecutionPrice,
      baseSpreadBps,
      sizeBasedSpreadBps,
      exceedsTolerance,
      currentTolerance: userTolerance,
      suggestedTolerance,
      isLoading: false,
      hasVlpData: true,
    };
  }, [orderSize, market, currentPrice, vlpQuote, userTolerance, isBuy]);
}
